import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { SummaryMetricCard, MetricGrid } from '@/components/global/SummaryMetricCard';
import { DateRangePicker, useDateRange } from '@/components/finance';
import { TrendingDown, Clock, AlertTriangle, Hash, Download } from 'lucide-react';
import { ProductIdentity } from '@/components/products';
import { LocationLink } from '@/components/locations';
import { downloadCsv } from '@/lib/csvExport';

interface OOSReportItem {
  productId: string;
  productSku: string;
  productName: string;
  productImageUrl: string | null;
  locationType: 'WAREHOUSE' | 'MACHINE';
  locationId: string;
  locationName: string;
  totalOOSEvents: number;
  totalOOSHours: number;
  averageOOSDuration: number;
  longestOOSEvent: number;
  lastOOSEndedAt: string | null;
  currentlyOOS: boolean;
}

interface OOSReport {
  period: { startDate: string; endDate: string };
  summary: {
    totalEvents: number;
    totalHours: number;
    averageDuration: number;
    currentlyOOSCount: number;
    productCount: number;
    locationCount: number;
  };
  items: OOSReportItem[];
}

function formatHours(hours: number): string {
  if (hours < 1) {
    return `${Math.round(hours * 60)}m`;
  }
  if (hours < 24) {
    return `${hours.toFixed(1)}h`;
  }
  return `${(hours / 24).toFixed(1)}d`;
}

export default function OOSReportPage() {
  const { dateRange, setDateRange } = useDateRange('L30D');
  const [report, setReport] = useState<OOSReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [locationType, setLocationType] = useState<string>('all');
  const [groupBy, setGroupBy] = useState<string>('product');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
        groupBy,
      });
      if (locationType && locationType !== 'all') params.set('locationType', locationType);

      const res = await apiFetch(`/api/v1/reports/oos?${params}`);
      if (res.ok) {
        const data = await res.json();
        setReport(data);
      }
    } catch (error) {
      console.error('Error loading OOS report:', error);
    } finally {
      setLoading(false);
    }
  }, [dateRange, locationType, groupBy]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  function exportCsv() {
    if (!report || report.items.length === 0) return;
    downloadCsv(
      `oos-report-${new Date().toISOString().slice(0, 10)}.csv`,
      [
        'Product',
        'SKU',
        'Location',
        'Location type',
        'Events',
        'Total hours',
        'Avg duration (hours)',
        'Longest (hours)',
        'Currently OOS',
      ],
      report.items.map((item) => [
        item.productName,
        item.productSku,
        item.locationName,
        item.locationType,
        item.totalOOSEvents,
        item.totalOOSHours.toFixed(2),
        item.averageOOSDuration.toFixed(2),
        item.longestOOSEvent.toFixed(2),
        item.currentlyOOS ? 'Yes' : 'No',
      ])
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="space-y-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-semibold">Out of Stock Report</h1>
          <div className="flex items-center gap-2">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <Button
              variant="outline"
              onClick={exportCsv}
              disabled={loading || !report || report.items.length === 0}
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
          </div>
        </div>

        <MetricGrid className="mb-6">
          <SummaryMetricCard
            label="Total OOS Events"
            value={report ? report.summary.totalEvents.toString() : '-'}
            icon={Hash}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Total OOS Hours"
            value={report ? formatHours(report.summary.totalHours) : '-'}
            icon={Clock}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Avg Duration"
            value={report ? formatHours(report.summary.averageDuration) : '-'}
            icon={TrendingDown}
            isLoading={loading}
          />
          <SummaryMetricCard
            label="Currently OOS"
            value={report ? report.summary.currentlyOOSCount.toString() : '-'}
            icon={AlertTriangle}
            status={report && report.summary.currentlyOOSCount > 0 ? 'red' : undefined}
            isLoading={loading}
          />
        </MetricGrid>

        <Card className="mb-6">
          <div className="p-4 flex items-center gap-4 border-b">
            <Select value={locationType} onValueChange={setLocationType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Locations</SelectItem>
                <SelectItem value="MACHINE">Machines</SelectItem>
                <SelectItem value="WAREHOUSE">Warehouses</SelectItem>
              </SelectContent>
            </Select>
            <Select value={groupBy} onValueChange={setGroupBy}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Group By" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="product">By Product</SelectItem>
                <SelectItem value="location">By Location</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="text-right">Events</TableHead>
                <TableHead className="text-right">Total Hours</TableHead>
                <TableHead className="text-right">Avg Duration</TableHead>
                <TableHead className="text-right">Longest</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : report?.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No OOS events found for this period
                  </TableCell>
                </TableRow>
              ) : (
                report?.items.map((item, idx) => (
                  <TableRow key={`${item.productId}-${item.locationId}-${idx}`}>
                    <TableCell>
                      <ProductIdentity
                        productId={item.productId}
                        name={item.productName}
                        sku={item.productSku}
                        imageUrl={item.productImageUrl}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell>
                      <div>
                        <LocationLink
                          locationType={item.locationType}
                          locationId={item.locationId}
                          name={item.locationName}
                        />
                      </div>
                      <div className="text-sm text-muted-foreground">{item.locationType}</div>
                    </TableCell>
                    <TableCell className="text-right">{item.totalOOSEvents}</TableCell>
                    <TableCell className="text-right">{formatHours(item.totalOOSHours)}</TableCell>
                    <TableCell className="text-right">{formatHours(item.averageOOSDuration)}</TableCell>
                    <TableCell className="text-right">{formatHours(item.longestOOSEvent)}</TableCell>
                    <TableCell>
                      {item.currentlyOOS ? (
                        <Badge variant="destructive">OOS</Badge>
                      ) : (
                        <Badge variant="outline">In Stock</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>
    </div>
  );
}
